import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { getAppType, userService, getHybridApp } from '@vmos2/core';
import { getKioskId } from '@vmos2/utils';

const originalLocation = `${document.location.protocol}//${document.location.hostname}${document.location.pathname}${document.location.search}`;

class AnalyticsService {
  constructor(userService) {
    this.userService = userService;
    this.eventsQueue = {};
    this.config = { tenant: {} };
    this.isReactNative = null;
  }

  init = async (bootstrap) => {
    this.config.tenant = bootstrap.tenant;
    this.isReactNative =  await getHybridApp();
  };

  isDelayedEvent = (eventType, storeUUID, menuUUID, currentStore, currentMenu) => {
    if (storeUUID) {
      return menuUUID ? isEmpty(currentStore) || isEmpty(currentMenu) : isEmpty(currentStore);
    }
    return false;
  };

  storeMenuKey = (store, menu) => `${store}/${menu}`;

  resetDataLayer = dl => {
    // eslint-disable-next-line func-names
    dl.push(function () {
      this.reset();
    });
    dl.push({ originalLocation });
  };


  sendAnalytics = (eventInfo) => {
    // web specific code
    if (!this.isReactNative) {
      const dl = this.getDataLayer();
      dl.push(eventInfo);
      return;
    }

    if (this.isReactNative?.sendFirebaseAnalytics) {
      this.isReactNative.sendFirebaseAnalytics(eventInfo.event, eventInfo.eventProperties);
    }

    if (this.isReactNative?.sendFacebookAnalytics) {
      this.isReactNative.sendFacebookAnalytics(eventInfo.event, eventInfo.eventProperties);
    }
  }

  getDataLayer = () => {
    if (!window.dataLayer) {
      window.dataLayer = [];
      window.dataLayer.push({ originalLocation });
    }
    return window.dataLayer;
  };

  getSystemType = () => {
    const appType = getAppType();
    return appType === 'online' ? 'preorder' : appType;
  };

  getDefaultEvent = async ({ store, menu }) => {
    const dl = this.getDataLayer();
    const kioskID = await getKioskId();
    const systemType = this.getSystemType();
    const isKiosk = systemType === 'kiosk';
    const user = this.userService.getUser();
    const lastScreenIndex = dl.reduce(
      (acc, event, i) => (get(event, 'eventProperties.eventType') === 'screen' ? i : acc),
      -1
    );
    const lastScreen = lastScreenIndex > -1 ? dl[lastScreenIndex] : undefined;
    return {
      systemType,
      tenantID: this.config.tenant.uuid,
      tenantName: this.config.tenant.name,
      storeID: get(store, 'uuid', null),
      storeName: get(store, 'name', null),
      menuID: get(menu, 'uuid', null),
      menuName: get(menu, 'name', null),
      userID: isKiosk || isEmpty(user) ? null : user.uuid,
      kioskID,
      previousScreen: get(lastScreen, 'eventProperties.properties', null),
    };
  };

  sendDelayedEvents = (currentStore, currentMenu) => {
    const dl = this.getDataLayer();
    const storeUUID = get(currentStore, 'uuid');
    const menuUUID = get(currentMenu, 'uuid');
    if (storeUUID) {
      const storeMenu = this.storeMenuKey(storeUUID, menuUUID);
      if (this.eventsQueue[storeMenu]) {
        this.eventsQueue[storeMenu].forEach(delayedEvent => {
          Object.assign(delayedEvent.eventProperties, {
            storeID: currentStore.uuid,
            storeName: currentStore.name,
            menuID: currentMenu.uuid,
            menuName: currentMenu.name,
          });
          this.resetDataLayer(dl);
          this.sendAnalytics(delayedEvent, dl);
        });
        this.eventsQueue[storeMenu] = undefined;
      }
      if (this.eventsQueue[storeUUID]) {
        this.eventsQueue[storeUUID].forEach(delayedEvent => {
          Object.assign(delayedEvent.eventProperties, {
            storeID: currentStore.uuid,
            storeName: currentStore.name,
          });
          this.resetDataLayer(dl);
          this.sendAnalytics(delayedEvent, dl);
        });
        this.eventsQueue[storeUUID] = undefined;
      }
    }
  };

  sendEvent = async (event, eventProperties = {}, { storeUUID, menuUUID, store, menu, isScreenEvent } = {}) => {
    const eventType = isScreenEvent ? 'screen' : 'action';
    const defaultEventProperties = await this.getDefaultEvent({ store, menu });
    eventProperties.properties = eventProperties.properties || {};
    eventProperties.properties.name = event;
    const ev = {
      event,
      eventProperties: { ...defaultEventProperties, ...eventProperties, eventType },
    };
    const dl = this.getDataLayer();
    if (this.isDelayedEvent(eventType, storeUUID, menuUUID, store, menu)) {
      const key = menuUUID ? this.storeMenuKey(storeUUID, menuUUID) : storeUUID;
      this.eventsQueue[key] = this.eventsQueue[key] || [];
      this.eventsQueue[key].push({ ...ev });
    } else {
      this.resetDataLayer(dl);
      this.sendAnalytics(ev);
    }
  };
}

export const analyticsService = new AnalyticsService(userService);
